import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { Grid, Typography, Button, Box, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List, CellMeasurer, CellMeasurerCache, WindowScroller } from 'react-virtualized';
import { useWindowSize } from 'common/hooks/useWindowSize';

import { OrderInfo } from 'common/types';
import { RentalsTabValues } from 'services/types';
import {
	addExpiredBookedStoreRentalsListener,
	isBookingsViewExpiredChecked,
	isBookingsViewDeliveryChecked,
} from 'actions';
import { getSearchResults } from 'common/utils/searchFunctions';
import EmptyState from 'common/components/EmptyState';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import BarCodeReader from 'components/BarCodeReader';

import { sortRentalsByLatestDate, sortRentalsByEarliestDate } from './sortUtils';
import RentalCardContainer from './BookedRentalCardContainer';
import {
	ROW_TYPES,
	generateRowsForBookedRentalsView,
	ReactVirtualizedRowForRentals,
} from '../rentalsViewReactVirtualizedUtils';

import { Routes, push } from 'routing';

import * as StockSelectors from 'selectors/StockSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import * as LanguageSelectors from 'selectors/LanguageSelectors';
import {
	buildRentalsOfTypeData,
	buildRentalsOfTypeLoading,
	buildRentalsOfTypeError,
	buildRentalsOfTypeAtSelectedLocation,
	rentalsAtSelectedLocationWithDeliveryTo,
	RentalsType,
	LocationType,
} from 'selectors/RentalsSelectors';
import { localFormatString } from 'common/utils/dateUtils';
import { useTranslation } from 'services/localization/useTranslation';
import RentalSearchFilter from '../RentalSearchBar/RentalSearchFilter';
import PaymentMethodBanner from 'components/PaymentMethodBanner';
import { useHasFeature } from 'hooks';

const ORDER_FETCH_LIMIT = 30;

const cache = new CellMeasurerCache({
	defaultHeight: 60,
	fixedWidth: true,
});

const rentalsSelector = buildRentalsOfTypeAtSelectedLocation(
	RentalsType.BOOKED,
	LocationType.START,
);

const rentalsLoadingSelector = buildRentalsOfTypeLoading(RentalsType.BOOKED);

const expiredRentalsSelector = buildRentalsOfTypeAtSelectedLocation(
	RentalsType.EXPIRED,
	LocationType.START,
);

const deliveryRentalsSelector = rentalsAtSelectedLocationWithDeliveryTo(
	RentalsType.BOOKED,
	LocationType.START,
);

const expiredDeliveryRentalsSelector = rentalsAtSelectedLocationWithDeliveryTo(
	RentalsType.EXPIRED,
	LocationType.START,
);

const allExpiredRentalsSelector = buildRentalsOfTypeData(RentalsType.EXPIRED);

const expiredRentalsLoadingSelector = buildRentalsOfTypeLoading(RentalsType.EXPIRED);
const expiredRentalsErrorSelector = buildRentalsOfTypeError(RentalsType.EXPIRED);

const BookedRentalsView = () => {
	const classes = useStyles();
	const { hasFeature } = useHasFeature();
	const windowSize = useWindowSize();
	const theme = useTheme();
	const isMobileScreenSize = useMediaQuery(theme.breakpoints.down('sm'));

	const dispatch = useDispatch();
	const rentals = useSelector(rentalsSelector);
	const rentalsLoading = useSelector(rentalsLoadingSelector);
	const expiredRentals = useSelector(expiredRentalsSelector);
	const deliveryRentals = useSelector(deliveryRentalsSelector);
	const expiredDeliveryRentals = useSelector(expiredDeliveryRentalsSelector);
	const expiredRentalsLoading = useSelector(expiredRentalsLoadingSelector);
	const expiredRentalsError = useSelector(expiredRentalsErrorSelector);
	const allExpiredRentalsCount = useSelector(allExpiredRentalsSelector).length;
	const lang = useSelector(LanguageSelectors.language);
	const pendingLinks = useSelector(ViewSelectors.pendingRentalLinks);
	const shopId = useSelector(ShopSelectors.activeShopId);
	const currentShopInfo = useSelector(ShopSelectors.activeShopPublicInfo);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const categories = useSelector(StockSelectors.categoriesData);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const dateFormatString = localFormatString('DD.MM.', shopDateFormat);
	const shopStoreUrl = useSelector(ShopSelectors.activeShopOnlineURL);
	const bookingsViewExpiredChecked = useSelector(ViewSelectors.bookingsViewExpiredChecked);
	const bookingsViewDeliveryChecked = useSelector(ViewSelectors.bookingsViewDeliveryChecked);
	const locationFilterType = useSelector(ViewSelectors.bookingsViewLocationType);
	const newRentalFeature = hasFeature('NEW_RENTAL');
	const activeLocation = useSelector(ShopSelectors.activeShopLocationUrlsById);
	const userLocation = useSelector(UserSelectors.userActiveLocationId)!;
	const mainlocation = useSelector(ShopSelectors.activeShopMainLocationId);
	const locationName = activeLocation[userLocation];
	const onlineStoreUrl =
		userLocation === mainlocation
			? `${shopStoreUrl}/shop?admin=true`
			: `${shopStoreUrl}/l/${locationName}?admin=true`;

	const [expiredRentalsLimit, setExpiredRentalsLimit] = useState(ORDER_FETCH_LIMIT);
	const [searchInput, setSearchInput] = useState('');
	const [filteredRentals, setFilteredRentals] = useState<null | OrderInfo[]>(null);
	const [reactVirtualizedRows, setReactVirtualizedRows] = useState<ReactVirtualizedRowForRentals[]>(
		[],
	);
	const { t } = useTranslation();
	const hasRentals = rentals.length !== 0 || expiredRentals.length !== 0;

	const rentalsForSearch = useMemo(() => {
		return bookingsViewExpiredChecked
			? bookingsViewDeliveryChecked
				? expiredDeliveryRentals
				: expiredRentals
			: bookingsViewDeliveryChecked
			? deliveryRentals
			: rentals;
	}, [
		bookingsViewExpiredChecked,
		bookingsViewDeliveryChecked,
		expiredDeliveryRentals,
		expiredRentals,
		deliveryRentals,
		rentals,
	]);

	const rentalsToRender = useMemo(() => {
		const sortFunc = bookingsViewExpiredChecked
			? sortRentalsByLatestDate
			: sortRentalsByEarliestDate;

		const getItems = () => {
			const rentalsToUse = filteredRentals ?? rentalsForSearch;
			return !!searchInput
				? getSearchResults(searchInput, rentalsToUse, ['NAME', 'PRODUCT_CODE', 'ORDER_BARCODE'])
				: rentalsToUse;
		};

		return getItems().sort(sortFunc);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rentalsForSearch, filteredRentals, searchInput]);

	useEffect(() => {
		cache.clearAll();
	}, [windowSize]);

	useEffect(() => {
		cache.clearAll();
		const bookedRentalRows = generateRowsForBookedRentalsView(
			rentalsToRender,
			!!bookingsViewExpiredChecked,
		);
		setReactVirtualizedRows(bookedRentalRows);
	}, [bookingsViewExpiredChecked, rentalsToRender]);

	useEffect(() => {
		if (allExpiredRentalsCount === expiredRentalsLimit && !hasRentals) {
			setExpiredRentalsLimit(expiredRentalsLimit + ORDER_FETCH_LIMIT);
		}
	}, [allExpiredRentalsCount, expiredRentalsLimit, hasRentals]);

	useEffect(() => {
		if (shopId) {
			dispatch(addExpiredBookedStoreRentalsListener(shopId, expiredRentalsLimit));
		}
	}, [expiredRentalsLimit, shopId, dispatch]);

	const handleBarCodeScan = (input: string) => {
		const results = getSearchResults(
			input,
			[...expiredRentals, ...rentals],
			['NAME', 'PRODUCT_CODE', 'ORDER_BARCODE'],
		);

		if (results.length === 1) {
			push(Routes.BookingPage(results[0].id));
		}
		setSearchInput(input);
	};

	const handleToggleExpired = () => {
		dispatch(isBookingsViewExpiredChecked(!bookingsViewExpiredChecked));
	};

	const handleToggleDelivery = () => {
		dispatch(isBookingsViewDeliveryChecked(!bookingsViewDeliveryChecked));
	};

	const renderExpiredRentalsLoading = () => {
		if (expiredRentalsLoading) return <Spinner in={true} />;
		if (expiredRentalsError) return expiredRentalsError;

		return null;
	};

	const renderFilterSegment = () => (
		<Grid
			container
			alignItems={isMobileScreenSize ? 'flex-start' : 'center'}
			justifyContent="flex-end"
		>
			<Grid item xs={8} sm>
				{(!!searchInput ||
					filteredRentals ||
					(bookingsViewDeliveryChecked && bookingsViewExpiredChecked)) && (
					<Typography variant="h6">
						{rentalsToRender.length} {t('rentals.amountOfRentals', 'Rentals were found')}
					</Typography>
				)}
			</Grid>
		</Grid>
	);

	const renderCardLabels = (dateStringAsMoment: moment.Moment) => {
		const formattedDateString = dateStringAsMoment.isSame(moment(), 'day')
			? dateStringAsMoment.format(`[${t('common:times.today')},] ${dateFormatString}`)
			: dateStringAsMoment.format(dateFormatString);
		return (
			<Grid
				container
				alignItems="flex-end"
				className={classes.cardLabelPadding}
				justifyContent="space-between"
			>
				<Grid item xs={6}>
					<Typography variant="h5">{formattedDateString}</Typography>
				</Grid>
			</Grid>
		);
	};

	const renderWalkinText = () => (
		<div className={classes.flexDiv}>
			<div className={classes.lines}></div>
			<Typography align="center" className={classes.header}>
				{t('rentalCard.walkIns', 'Walk-in customers')}
			</Typography>
			<div className={classes.lines}></div>
		</div>
	);

	const renderReservationsText = () => (
		<div className={classes.flexDiv}>
			<div className={classes.lines}></div>
			<Typography align="center" className={classes.header}>
				{t('rentalCard.reservations', 'Reservations')}
			</Typography>
			<div className={classes.lines}></div>
		</div>
	);

	const renderRentalCard = (rental: OrderInfo, index: number) => {
		const rentalTab = RentalsTabValues.BOOKED;
		const rentalCardProps = {
			rental,
			index,
			rentalTab,
			stockProducts,
			categories,
			currentShopInfo: currentShopInfo!,
			language: lang,
			pendingLinks,
			showExpired: bookingsViewExpiredChecked ?? false,
			showLocations: locationFilterType === 'all',
			delivery: rental?.services?.delivery,
		};

		return <RentalCardContainer {...rentalCardProps} />;
	};

	const renderNoRentalsToday = () => (
		<Typography align="center" className={classes.header}>
			{t('rentals.noBookingsToday2', 'No orders for today')}
		</Typography>
	);

	const renderMoreExpiredRentalsButton = () => (
		<div>
			{allExpiredRentalsCount >= expiredRentalsLimit ? (
				<Button
					variant="contained"
					color="primary"
					className={classes.moreExpiredResultsButton}
					onClick={() => {
						setExpiredRentalsLimit(expiredRentalsLimit + ORDER_FETCH_LIMIT);
					}}
				>
					{t('rentals.loadMoreRentalsBtn', 'Load more rentals')}
				</Button>
			) : null}
			{renderExpiredRentalsLoading()}
		</div>
	);

	const rowRenderer = ({
		index,
		key,
		style,
		parent,
	}: {
		index: number;
		key: string;
		style: any;
		parent: any;
	}) => {
		const {
			DATE_TEXT,
			WALKIN_TEXT,
			RESERVATIONS_TEXT,
			RENTAL_CARD,
			NO_RENTALS_TODAY,
			MORE_EXPIRED_RENTALS_BUTTON,
		} = ROW_TYPES;

		const rowToRender: ReactVirtualizedRowForRentals = reactVirtualizedRows[index];
		const isLastRow = index === reactVirtualizedRows.length - 1;

		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
				<div style={style}>
					<Container
						className={isLastRow ? classes.reactVirtualizedLastRow : classes.reactVirtualizedRow}
						fluid
					>
						{rowToRender.type === DATE_TEXT ? (
							renderCardLabels(rowToRender.date)
						) : rowToRender.type === WALKIN_TEXT ? (
							renderWalkinText()
						) : rowToRender.type === RESERVATIONS_TEXT ? (
							renderReservationsText()
						) : rowToRender.type === RENTAL_CARD ? (
							renderRentalCard(rowToRender.data, index)
						) : rowToRender.type === NO_RENTALS_TODAY ? (
							renderNoRentalsToday()
						) : rowToRender.type === MORE_EXPIRED_RENTALS_BUTTON ? (
							renderMoreExpiredRentalsButton()
						) : (
							<div>{'ROW TYPE NOT DEFINED'}</div>
						)}
					</Container>
				</div>
			</CellMeasurer>
		);
	};

	return (
		<EmptyState
			title={t('rentals.empty_states.bookingsEmpty2', 'You have no upcoming orders')}
			description={
				newRentalFeature
					? t(
							'rentals.empty_states.bookingsEmptyDescription2',
							`Once you create your first order, you'll be able to manage it here!`,
					  )
					: t(
							'rentals.empty_states.bookingsEmptyDescriptionLite2',
							'Create your first order via online shop and it will appear here',
					  )
			}
			callToActionText={
				newRentalFeature
					? t('rentals.empty_states.bookingsEmptyCTA2', 'Create an order')
					: t('rentals.empty_states.bookingsEmptyCTALite2', 'Create online order')
			}
			callToActionHandler={
				newRentalFeature
					? () => push(Routes.NEW_RENTAL)
					: () => window.open(onlineStoreUrl, '_blank')
			}
			isLoading={rentalsLoading}
			isEmpty={!hasRentals}
			render={() => (
				<BarCodeReader barCodeRead={handleBarCodeScan}>
					<Container className={classes.filterAndResultsSegment} fluid>
						<PaymentMethodBanner />
						<Box mb={1} mt={1}>
							<RentalSearchFilter
								rentals={rentalsForSearch}
								onFilteredRentalsChange={(newFilteredRentals: null | OrderInfo[]) =>
									setFilteredRentals(newFilteredRentals)
								}
								searchInput={searchInput}
								onSearchInputChange={setSearchInput}
								onToggleExpired={handleToggleExpired}
								expiredRentalCount={expiredRentals.length}
								expiredRentalLimit={expiredRentalsLimit}
								onToggleDelivery={handleToggleDelivery}
								deliveryRentalsCount={deliveryRentals.length}
								mode="bookings"
							/>
						</Box>
						{renderFilterSegment()}
					</Container>
					<div className={classes.reactVirtualizedContainer}>
						<WindowScroller>
							{({ height, onChildScroll, scrollTop }) => (
								<AutoSizer disableHeight>
									{({ width }) => (
										<List
											style={{ outline: 'none' }}
											autoHeight
											width={width}
											height={height}
											rowCount={reactVirtualizedRows.length}
											rowHeight={cache.rowHeight}
											rowRenderer={rowRenderer}
											scrollTop={scrollTop}
											onScroll={onChildScroll}
											overscanRowCount={2}
										/>
									)}
								</AutoSizer>
							)}
						</WindowScroller>
					</div>
				</BarCodeReader>
			)}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		'@global': {
			html: {
				overflowY: 'scroll',
				scrollbarWidth: 'none',
				'-ms-overflow-style': 'none',
			},
			'html::-webkit-scrollbar': {
				width: 0,
				height: 0,
			},
		},
		filterAndResultsSegment: {
			height: 'auto',
			flex: 0,
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(2),
				paddingBottom: theme.spacing(1),
			},
		},
		reactVirtualizedContainer: {
			paddingTop: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(0),
			},
		},
		reactVirtualizedRow: {
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(0.5),
		},
		reactVirtualizedLastRow: {
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(10),
			[theme.breakpoints.down('sm')]: {
				paddingBottom: theme.spacing(8),
			},
		},
		searchFilters: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(1),
		},
		showExpiredLabel: {
			[theme.breakpoints.down('sm')]: {
				fontSize: '1rem',
			},
		},
		moreExpiredResultsButton: {
			marginTop: theme.spacing(2),
		},
		rentalContainer: {
			paddingBottom: theme.spacing(2),
		},
		searchResultsContainer: {
			display: 'flex',
			alignItems: 'center',
			[theme.breakpoints.down('sm')]: {
				justifyContent: 'space-between',
			},
		},
		clearResultsBtn: {
			height: 40,
			marginLeft: '24px',
			[theme.breakpoints.down('sm')]: {
				marginLeft: 0,
			},
		},
		cardLabelPadding: {
			padding: theme.spacing(5, 2, 1, 0),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(1),
			},
		},
		hiddenXs: {
			[theme.breakpoints.down('sm')]: {
				display: 'none',
			},
		},
		hiddenSm: {
			[theme.breakpoints.down('md')]: {
				display: 'none',
			},
		},
		mobileSpacing: {
			[theme.breakpoints.down('sm')]: {
				marginTop: theme.spacing(0),
			},
		},
		flexDiv: {
			display: 'flex',
			alignItems: 'center',
		},
		header: {
			padding: theme.spacing(2),
			color: theme.palette.colors.tundra.main,
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(1),
			},
		},
		lines: {
			border: `1px solid ${theme.palette.divider}`,
			height: '1px',
			width: '50%',
		},
	}),
);

export default BookedRentalsView;
